import { createBackendApi } from "../../backend/client"
import { createHttpTransport } from "../../backend/httpTransport"
import { getMyOrganization } from "../../commands/organizations"
import { AppSupabaseClient, OrganizationRow } from "../../types/supabase"
import { present } from "../util/present"

type SelectOrganizationForUserParams = {
  /**
   * When present, allow resolving the organization by the email's domain (and by
   * promo code redemption). Pass null to restrict the lookup to organizations the
   * user is already linked to through organization_memberships.
   *
   * The value itself is no longer sent anywhere — the backend reads the caller's
   * email from their JWT. Only its presence still matters, as the signal for which
   * lookup scope the caller wants.
   */
  email: string | null | undefined
  id?: string | null | undefined
}

/**
 * Resolves the signed-in user's organization via GET /backend/organizations/mine.
 *
 * This used to query PostgREST directly, which is why public.organizations carried
 * a world-readable `using (true)` select policy: the email-domain branch runs
 * during onboarding, before the user has any membership_roles row, so no row
 * predicate could authorize it. The lookup moved server-side so that policy could
 * be scoped down — see
 * supabase/migrations/20260729000000_scope_organizations_rls.sql.
 *
 * The endpoint intentionally accepts no email or user id: both come from the JWT,
 * so it cannot be used to probe whether an arbitrary domain belongs to a customer.
 */
export async function selectOrganizationForUser(
  client: AppSupabaseClient,
  {email, id: userId}: SelectOrganizationForUserParams
): Promise<OrganizationRow | null> {
  // Nothing to resolve for a caller with neither identifier — matches the previous
  // behavior, where every branch was individually gated on one of these.
  if (!present(userId) && !present(email)) { return null }

  const { data: { session } } = await client.auth.getSession()
  if (!session?.access_token) { return null }

  const api = createBackendApi(createHttpTransport({
    getAccessToken: async () => (await client.auth.getSession()).data.session?.access_token,
  }))
  const scope = present(email) ? 'all' : 'membership'
  const body = await getMyOrganization(api, scope)
  return (body.organization as OrganizationRow | null) ?? null
}
